import React from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { observer, Provider } from "mobx-react";
import ThemeWrapper from "../components/styles/ThemeWrapper/index.js";
import ErrorBoundary from "../components/errors/ErrorBoundary";
import Flexbox from "flexbox-react";
import EmployeeSidebar from "../components/onboarding_process/EmployeeSidebar";
import EmployeeOnboardingDetails from "../components/onboarding_process/EmployeeOnboardingDetails.js";
import ToastStore from "../stores/ToastStore";
import PageHeading from "../components/goals/PageHeading.js";
import "../components/onboarding_process/OnboardingStepsView.css";
import CurrentUserStore from "../stores/CurrentUserStore";
import ScrollableDropdown from "../components/shared/ScrollableDropdown.jsx";
import EmptyStateNotice from "../components/goal_performance/EmptyStateNotice.js";
import Pagination from "react-js-pagination";

export default class OnboardingStepsView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      search: "",
      selectedEmployee: null,
      onboardingEmployees: [],
      onboardingSteps: [],
      checkins: [],
      selectedCheckin: null,
      activePage: 1,
      perPage: 15,
      totalCount: 0,
      totalPages: 0
    };
  }


  componentDidMount() {
    this.initializeData();
  }
  initializeData = async () => {
    const { employeeId, checkinId } = this.getUrlParams();
    await this.fetchCheckins(checkinId, employeeId);
  };

  getUrlParams = () => {
    const params = new URLSearchParams(window.location.search);

    let employeeId = null;
    let checkinId = null;

    try {
      const emp = params.get("emp");
      const checkin = params.get("checkin");

      if (emp) employeeId = atob(emp);
      if (checkin) checkinId = atob(checkin);
    } catch (e) {
      console.error("Error decoding URL params:", e);
    }

    return { employeeId, checkinId };
  };

  fetchCheckins = async (urlCheckinId, urlEmployeeId) => {
    try {
      const response = await axios.get(`${Cookies.get("apiEnv")}/checkins/list`);
      const checkins = response.data || [];

      if (!checkins.length) {
        this.setState({ checkins: [], selectedCheckin: null, isLoading: false });
        return;
      }
      // select from URL or fallback
      const selectedCheckin = checkins.find((c) => c.id == urlCheckinId) || checkins[0];
      this.setState(
        {
          checkins,
          selectedCheckin,
          isLoading: false,
        },
        () => {
          this.fetchOnboardingEmployees(selectedCheckin.id, urlEmployeeId);
        }
      );
    } catch (error) {
      console.error("Error fetching checkins:", error);
      this.setState({ isLoading: false });
    }
  };

  fetchOnboardingEmployees = async (
    checkinId,
    urlEmployeeId,
    page = this.state.activePage
  ) => {
    try {
      const response = await axios.get(
        `${Cookies.get("apiEnv")}/onboarding_users`,
        {
          params: {
            checkin_id: checkinId,
            page,
            per_page: this.state.perPage
          }
        }
      );

      const { data, meta } = response.data;

      const employees = data.map((emp) => ({
        id: emp.user_id.id,
        name: emp.user_id?.fullName || "N/A",
        email: emp.user_id?.email,
        imageFileName: emp.user_id?.imageFileName,
        role: emp.user_id?.job_profile_name || "Employee",
        completed_tasks: emp.user_id?.completed_tasks || 0,
        total_tasks: emp.user_id?.total_tasks || 0,
        tasks_percent_complete:
          emp.user_id?.tasks_percent_complete || 0,
      }));

      const selectedEmployee =
        employees.find((e) => e.id === urlEmployeeId) ||
        employees[0] ||
        null;

      this.setState({
        onboardingEmployees: employees,
        selectedEmployee,
        activePage: meta.page,
        totalCount: meta.total_count,
        totalPages: meta.total_pages
      });

      if (selectedEmployee) {
        this.fetchEmployeeSteps(selectedEmployee.id);
      }
    } catch (error) {
      console.error(error);
    }
  };

  handlePageChange = (pageNumber) => {
    this.fetchOnboardingEmployees(
      this.state.selectedCheckin.id,
      null,
      pageNumber
    );
  };

  canUserEditStep = (step) => {
    const currentUser = CurrentUserStore.currentUser;
    if (!currentUser) return false;
    const { isAccountOwner, job_profile } = currentUser;
    // Account owner can always edit
    if (isAccountOwner) return true;
    if (!step.job_profile_id) return false;
    return step.job_profile_id === job_profile.id;
  };

  toggleTaskStatus = async (stepId, taskId) => {
    const { onboardingSteps, selectedEmployee } = this.state;
    const currentUser = CurrentUserStore.currentUser;

    if (!selectedEmployee || !currentUser) return;
    const step = onboardingSteps.find((s) => s.id === stepId);
    if (!this.canUserEditStep(step)) {
      ToastStore.showToast("You are not allowed to update this task", "danger");
      return;
    }
    if (!selectedEmployee) return;

    // Deep clone for rollback
    const previousSteps = JSON.parse(JSON.stringify(onboardingSteps));
    let updatedTaskStatus = false;

    // Update tasks within the selected step
    const updatedSteps = onboardingSteps.map((step) => {
      if (step.id !== stepId) return step;

      const updatedTasks = step.tasks.map((task) => {
        if (task.id === taskId) {
          updatedTaskStatus = !task.status;
          return { ...task, status: updatedTaskStatus };
        }
        return task;
      });

      // Step status is derived from its tasks (not shown in UI)
      const stepCompleted =
        updatedTasks.length > 0 &&
        updatedTasks.every((task) => task.status);

      return {
        ...step,
        tasks: updatedTasks,
        status: stepCompleted
      };
    });

    // Optimistic UI update
    this.setState({ onboardingSteps: updatedSteps });
    const payload = {
      onboarding_step_id: stepId,
      onboarding_task_id: taskId,
      user_id: selectedEmployee.id,
      status: updatedTaskStatus ? 1 : 0
    };

    try {
      await axios.post(
        `${Cookies.get("apiEnv")}/onboarding_users/update_step_status`,
        payload
      );
    } catch (error) {
      console.error("Error updating step status:", error);

      // Revert UI on failure
      this.setState({ onboardingSteps: previousSteps });
    }
  };

  fetchEmployeeSteps = async (employeeId) => {
    try {
      this.setState({ isLoadingSteps: true });
      if (!this.state.selectedCheckin) return;
      const response = await axios.get(
        `${Cookies.get("apiEnv")}/onboarding_users/user_step_details/${employeeId}?checkin_id=${this.state.selectedCheckin.id}`
      );
      this.setState({
        onboardingSteps: response.data.onboarding_steps || [],
        userDetails: response.data.user_details,
        isLoadingSteps: false
      });
    } catch (error) {
      console.error("Error fetching onboarding steps:", error);
      this.setState({ isLoadingSteps: false });
    }
  };

  handleEmployeeSelect = (employee) => {
    this.setState({ selectedEmployee: employee });
    this.fetchEmployeeSteps(employee.id);
  };
  handleSelectCheckin = async (checkin) => {
    await this.setState(
      {
        selectedCheckin: checkin,
        selectedEmployee: null,
        onboardingEmployees: [],
        onboardingSteps: [],
      },
    );
    await this.fetchOnboardingEmployees(checkin.id);
  };

  render() {
    const stores = {
      ToastStore,
      CurrentUserStore,
    };

    const {
      onboardingEmployees,
      selectedEmployee,
      checkins,
      selectedCheckin,
      search,
      onboardingSteps,
      isLoading,
    } = this.state;

    const filteredEmployees = onboardingEmployees.filter((emp) =>
      emp.name.toLowerCase().includes(search.toLowerCase())
    );

    return (

      <Provider {...stores}>
        <ThemeWrapper>
          <ErrorBoundary>
            <Flexbox flexGrow={1} width="100hw" alignContent="stretch" flexDirection="column" paddingBottom="0px">

              {!isLoading ? (

                <Flexbox flexGrow={1} flexDirection="column" className="fixed-width-container" marginTop="20px">
                  <PageHeading
                    text="Check-in Process"
                    description="Complete your performance evaluation and reflect on your achievements and growth opportunities."
                  />
                  <Flexbox display="flex" alignItems="center" justifyContent="flex-end" flexDirection="row" gap="20px" className="push-10-b">
                    <ScrollableDropdown
                      width={300}
                      label={selectedCheckin?.title || "Select Checkin"}
                      items={checkins}
                      getKey={(e) => e.id}
                      getLabel={(e) => e.title}
                      onSelect={(checkin) => this.handleSelectCheckin(checkin)}
                    />
                  </Flexbox>
                  {onboardingEmployees.length > 0 ? (<div className="onboarding-container">
                    <div style={{ width: "290px" }}>
                      <EmployeeSidebar
                        employees={filteredEmployees}
                        search={search}
                        selectedEmployee={selectedEmployee}
                        onSearchChange={(value) =>
                          this.setState({ search: value })
                        }
                        onSelectEmployee={(emp) =>
                          this.handleEmployeeSelect(emp)
                        }
                      />

                      {this.state.totalCount > this.state.perPage && (
                        <Flexbox className="pagination-wrapper" justifyContent="flex-start" marginTop="20px">
                         
                          <Pagination
                            activePage={this.state.activePage}
                            itemsCountPerPage={this.state.perPage}
                            totalItemsCount={this.state.totalCount}
                            pageRangeDisplayed={3}
                            onChange={this.handlePageChange}
                            innerClass="pagination"
                            itemClass="page-item"
                            linkClass="page-link"
                          />
                        </Flexbox>
                      )}
                    </div>

                    <div className="onboarding-content">
                      {filteredEmployees.length === 0 ? (
                        <EmptyStateNotice
                          title="No employees found."
                          description="Try a different search to view an employee's onboarding details."
                        />
                      ) : selectedEmployee ? (
                        <EmployeeOnboardingDetails
                          employee={this.state.selectedEmployee}
                          steps={onboardingSteps}
                          toggleTaskStatus={this.toggleTaskStatus}
                          isLoading={this.state.isLoadingSteps}
                        />
                      ) : (
                        <EmptyStateNotice title="Select an employee to view onboarding details." description="Select an employee to view onboarding details." />
                      )}
                    </div>
                  </div>) : (<EmptyStateNotice title="No employees found in the pipeline" description="Add employees to the pipeline to get started" />)}
                </Flexbox>

              ) : (
                <Flexbox flexGrow={1} flexDirection="column" justifyContent="center" alignItems="center" >
                  <img style={{ height: "35px", width: "35px" }} src="https://s01.live2support.com/dashboardv2/chatassets/img/loader_new.gif" alt="Loading..." />
                </Flexbox>
              )}

            </Flexbox>
          </ErrorBoundary>
        </ThemeWrapper>
      </Provider>
    );
  }
}
